import { useState } from "react";
import type { CreateRequestBody } from "../../../services/leadService";

interface Props {
  onSubmit: (body: CreateRequestBody) => Promise<void>;
  submitting: boolean;
  error: string;
  isVerified: boolean | null;
  appDownloadUrl: string;
}

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const INTENTS = [
  { value: "long-term", label: "Long-term" },
  { value: "short-term", label: "Short-term" },
  { value: "friendship", label: "Friendship" },
  { value: "open", label: "Open to anything" },
];

export function RequestForm({
  onSubmit,
  submitting,
  error,
  isVerified,
  appDownloadUrl,
}: Props) {
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [city, setCity] = useState("");
  const [datingIntent, setDatingIntent] = useState("");
  const [interestsInput, setInterestsInput] = useState("");
  const [occupation, setOccupation] = useState("");

  const interests = interestsInput
    .split(",")
    .map((i) => i.trim())
    .filter(Boolean);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: CreateRequestBody = {};
    if (gender) body.gender = gender;
    if (minAge) body.minAge = Number(minAge);
    if (maxAge) body.maxAge = Number(maxAge);
    if (city.trim()) body.city = city.trim();
    if (datingIntent) body.datingIntent = datingIntent;
    if (interests.length) body.interests = interests;
    if (occupation.trim()) body.occupation = occupation.trim();
    void onSubmit(body);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {isVerified === false ? (
        <div className="p-4 rounded-2xl bg-blush border border-rose/20 space-y-3">
          <p className="text-[13px] font-semibold text-rose-dark">
            Verification required
          </p>
          <p className="text-[12px] text-rose-dark leading-relaxed">
            Only verified users can submit match requests. Complete verification
            in the app first, then come back here to submit.
          </p>
          <a
            href={appDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-magenta text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity no-underline"
          >
            Download the app to verify →
          </a>
        </div>
      ) : (
        <div className="p-4 rounded-2xl bg-blush border border-rose/20">
          <p className="text-[13px] text-rose-dark leading-relaxed">
            All fields are optional. The more you fill in, the better we can
            match you. A matchmaker will introduce candidates to you one at a
            time.
          </p>
        </div>
      )}

      <div>
        <label className="block text-[13px] font-semibold text-black mb-2">
          Looking for
        </label>
        <div className="grid grid-cols-2 gap-2">
          {GENDERS.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => setGender(g.value as "male" | "female")}
              className={`py-3 rounded-2xl border text-sm font-medium transition-all ${
                gender === g.value
                  ? "border-rose bg-blush text-black"
                  : "border-black/10 bg-white text-muted hover:border-black/20"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-semibold text-black mb-2">
            Min age
          </label>
          <input
            type="number"
            min={18}
            max={99}
            value={minAge}
            onChange={(e) => setMinAge(e.target.value)}
            placeholder="25"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
          />
        </div>
        <div>
          <label className="block text-[13px] font-semibold text-black mb-2">
            Max age
          </label>
          <input
            type="number"
            min={18}
            max={99}
            value={maxAge}
            onChange={(e) => setMaxAge(e.target.value)}
            placeholder="35"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-semibold text-black mb-2">
          City
        </label>
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Blantyre"
          className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
        />
      </div>

      <div>
        <label className="block text-[13px] font-semibold text-black mb-2">
          What you're looking for
        </label>
        <div className="grid grid-cols-2 gap-2">
          {INTENTS.map((i) => (
            <button
              key={i.value}
              type="button"
              onClick={() => setDatingIntent(i.value)}
              className={`py-3 rounded-2xl border text-[13px] font-medium transition-all ${
                datingIntent === i.value
                  ? "border-rose bg-blush text-black"
                  : "border-black/10 bg-white text-muted hover:border-black/20"
              }`}
            >
              {i.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-semibold text-black mb-2">
          Interests
        </label>
        <input
          type="text"
          value={interestsInput}
          onChange={(e) => setInterestsInput(e.target.value)}
          placeholder="Music, Travel, Cooking (comma-separated)"
          className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
        />
        {interests.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {interests.map((i) => (
              <span
                key={i}
                className="text-[12px] bg-neutral-100 text-black px-3 py-1 rounded-full"
              >
                {i}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-[13px] font-semibold text-black mb-2">
          Occupation
        </label>
        <input
          type="text"
          value={occupation}
          onChange={(e) => setOccupation(e.target.value)}
          placeholder="Engineer"
          className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
        />
      </div>

      {error && <p className="text-[13px] text-rose-dark">{error}</p>}

      <button
        type="submit"
        disabled={submitting || isVerified === false}
        className="w-full bg-magenta text-white text-sm font-semibold py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting
          ? "Submitting…"
          : isVerified === false
            ? "Verify in app to submit"
            : "Submit request"}
      </button>
    </form>
  );
}
